// Visit presentation helpers: independent state badges and site-local time formatting.
// Status is always written out in text; colour only reinforces it.
import type { VisitSummary } from '@passage/contracts';
import { useI18n } from '../i18n.tsx';

const TONES: Record<string, Record<string, string>> = {
  planning: { draft: 'blue', submitted: 'green', cancelled: 'red', expired: '' },
  approval: { not_required: '', pending: 'amber', approved: 'green', rejected: 'red', expired: 'amber', invalidated: 'amber' },
  evidence: { missing: '', accepted: 'green', rejected: 'red', expired: 'amber', review_pending: 'amber', unavailable: 'amber' },
  presence: { no_observation: '', observed_onsite: 'green', observed_departed: 'blue', unknown_conflicting: 'amber' },
  credential: { none: '', issued_active: 'green', revoke_pending: 'amber', unknown: 'red', failed: 'red', revoked: 'blue' },
  task: { waiting: '', pending: 'amber', blocked: 'red', approved: 'green', rejected: 'red', invalidated: '', closed: '' },
};

export function StateBadge({ domain, value }: { domain: 'planning' | 'approval' | 'evidence' | 'presence' | 'credential' | 'task'; value: string }) {
  const { tk } = useI18n();
  const key = domain === 'planning' ? `visit.planning.${value}` : domain === 'task' ? `task.status.${value}` : `state.${domain}.${value}`;
  return <span className={`badge ${TONES[domain]?.[value] ?? ''}`}>{tk(key, undefined, value)}</span>;
}

/** Format an instant in the visit's own zone (never the viewer's browser zone). */
export function useZoned() {
  const { dateTime, date } = useI18n();
  return {
    when: (iso: string, zone: string) => dateTime(iso, { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    time: (iso: string, zone: string) => dateTime(iso, { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    day: (iso: string, zone: string) => date(iso, { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
  };
}

/** The zone's UTC offset at an instant ("GMT+4"), used to flag a clock change inside a window (PB-12-10). */
function offsetAt(iso: string, zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date(iso)).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** "Mon 2 Nov, 10:00 – 11:00" or a multi-day summary, in the visit's zone. */
export function VisitWhen({ v }: { v: Pick<VisitSummary, 'startsAt' | 'endsAt' | 'timeZone' | 'scheduleMode'> }) {
  const { t, date, locale } = useI18n();
  const z = useZoned();
  if (!v.startsAt || !v.endsAt) return <span className="tiny">{t('visits.noSchedule')}</span>;
  const sameDay = z.day(v.startsAt, v.timeZone) === z.day(v.endsAt, v.timeZone);
  const shortDay = (iso: string) => date(iso, { timeZone: v.timeZone, weekday: 'short', day: 'numeric', month: 'short' });
  const sep = locale === 'ar' ? '، ' : ', ';
  // Time ranges are isolated left-to-right so Arabic layout never reverses them (PD-012).
  return (
    <span>
      {sameDay ? (
        <>
          {shortDay(v.startsAt)}
          {sep}
          <bdi dir="ltr">{`${z.time(v.startsAt, v.timeZone)}–${z.time(v.endsAt, v.timeZone)}`}</bdi>
        </>
      ) : (
        <>
          {shortDay(v.startsAt)} <bdi dir="ltr">{z.time(v.startsAt, v.timeZone)}</bdi> – {shortDay(v.endsAt)} <bdi dir="ltr">{z.time(v.endsAt, v.timeZone)}</bdi>
        </>
      )}
      {v.scheduleMode === 'daily' ? <small>{t('vform.mode.daily')}</small> : null}
      {!sameDay && v.scheduleMode !== 'daily' ? <small>{t('visits.overnight')}</small> : null}
      {offsetAt(v.startsAt, v.timeZone) !== offsetAt(v.endsAt, v.timeZone) ? <small>{t('visits.clockChange', { from: offsetAt(v.startsAt, v.timeZone), to: offsetAt(v.endsAt, v.timeZone) })}</small> : null}
    </span>
  );
}

export function ApprovalSummary({ c }: { c: VisitSummary['approvalCounts'] }) {
  const { t } = useI18n();
  if (c.pending + c.approved + c.rejected === 0) return <span className="tiny">{t('visits.noApproval')}</span>;
  return (
    <span className="tiny" style={{ color: 'inherit' }}>
      {t('visits.approvalSummary', { approved: c.approved, pending: c.pending, rejected: c.rejected })}
    </span>
  );
}
